import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { mealsApi } from '@/shared/api/endpoints';
import type { Meal, MealType } from '@/shared/api/types';
import { fromLocalInputValue, toLocalInputValue } from '@/shared/format/dates';
import { MEAL_TYPES, mealTypeLabel } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, Chip, Eyebrow, Field } from '@/shared/ui';
import { BackIcon } from '@/shared/ui/icons';
import { ItemRow } from '@/shared/ui/ItemRow';
import { EsqueletoComidas, useEsqueleto } from '@/shared/ui/Skeleton';

/**
 * RF-07 · hasta dónde se puede correr una comida hacia atrás.
 *
 * El mismo número que valida el servidor. Está duplicado a propósito y no compartido: el de allá es
 * la regla, éste sólo decide si un botón se ofrece. Si algún día divergen, gana el servidor y el
 * peor caso es un botón que devuelve un error legible — que es mucho mejor que el inverso, una
 * pantalla que impide algo que el sistema sí permite.
 */
const DIAS_HACIA_ATRAS = 30;

/** La misma comida corrida `dias`, conservando la hora. */
function moverDias(meal: Meal, dias: number): Promise<Meal> {
  const movida = new Date(meal.occurredAt);
  movida.setDate(movida.getDate() + dias);

  return mealsApi.update(meal.id, movida.toISOString(), meal.type, meal.note);
}

/**
 * Si correr la comida `dias` la deja dentro de la ventana que admite RF-07.
 *
 * Exportada para poder probarla: es la que decide si un control se ofrece, y equivocarse acá tiene
 * dos formas y sólo una se nota. Ofrecer de más da un error legible del servidor; ofrecer de menos
 * es un botón apagado sobre algo que el sistema sí permite, y eso nadie lo reporta.
 */
export function puedeCorrer(occurredAt: string, dias: number): boolean {
  const destino = new Date(occurredAt);
  destino.setDate(destino.getDate() + dias);

  const ahora = Date.now();
  const piso = ahora - DIAS_HACIA_ATRAS * 24 * 60 * 60 * 1000;

  return destino.getTime() <= ahora && destino.getTime() >= piso;
}

/**
 * La comida como unidad: cuándo fue, de qué tipo, qué ítems la componen.
 *
 * Todo lo que se hace *con* un ítem vive en su detalle. Acá sólo quedan las acciones que son de
 * la comida entera —mover la hora, duplicarla, borrarla— porque son las únicas que no tienen
 * sentido a nivel de un ítem suelto.
 */
export function MealDetailPage() {
  const { mealId = '' } = useParams();
  const navigate = useNavigate();
  const volver = useVolver('/');
  const loaded = useAsync<Meal>(() => mealsApi.get(mealId), [mealId]);

  const [meal, setMeal] = useState<Meal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [newItemText, setNewItemText] = useState('');

  useEffect(() => {
    if (loaded.data !== null) {
      setMeal(loaded.data);
    }
  }, [loaded.data]);

  // El esqueleto no se muestra apenas arranca la lectura: por debajo del umbral
  // aparecería y se iría antes de que el ojo lo registre, que es un parpadeo.
  const esqueleto = useEsqueleto(loaded.loading);

  /*
   * Mientras la lectura está en curso NO se puede decir que el ítem no existe.
   *
   * Acá la guarda estaba atada al esqueleto, que aparece recién a los 260 ms a propósito, así que
   * entre abrir la pantalla y ese umbral la ejecución caía en la rama de «no se encontró» y
   * pintaba un error rojo que era falso. Con la API local respondiendo en veinte milisegundos no
   * se veía; con la latencia de un teléfono, sí — medido: cuatrocientos milisegundos de error
   * antes del esqueleto.
   *
   * El esqueleto decide QUÉ se muestra mientras se carga, no SI se está cargando. Por debajo del
   * umbral no se muestra nada, que es lo que evita el parpadeo.
   */
  if (loaded.loading && meal === null) {
    return esqueleto ? <EsqueletoComidas comidas={1} /> : null;
  }

  if (meal === null) {
    return <Banner tone="danger">{loaded.error ?? 'No se encontró esta comida.'}</Banner>;
  }

  const current = meal;

  const run = async (action: () => Promise<Meal>) => {
    setError(null);
    try {
      setMeal(await action());
    } catch (cause) {
      setError(describeError(cause));
    }
  };

  const remove = async () => {
    setError(null);
    try {
      await mealsApi.remove(current.id);
      navigate('/', { replace: true });
    } catch (cause) {
      setError(describeError(cause));
    }
  };

  return (
    <>
      <header className="row-between">
        <button
          type="button"
          className="btn-square"
          aria-label="Volver"
          onClick={volver}
        >
          <BackIcon size={18} />
        </button>
        <h1 style={{ fontSize: 'var(--text-h2)' }}>{mealTypeLabel(current.type)}</h1>
        <span style={{ width: 38 }} />
      </header>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Card>
        <Field
          label="Cuándo"
          type="datetime-local"
          value={toLocalInputValue(current.occurredAt)}
          onChange={(event) =>
            void run(() =>
              mealsApi.update(
                current.id,
                fromLocalInputValue(event.target.value),
                current.type,
                current.note,
              ),
            )
          }
        />

        {/*
          Pasarla a otro día sin pelear con el selector.

          Mover una comida en el tiempo cambia a qué día computan sus ítems (RN-10), y eso ya
          funcionaba: el campo de arriba lo hace. Pero la operación real es «esto fue ayer, no hoy»
          —comer y registrar no pasan al mismo tiempo— y resolverla con un selector de fecha y hora
          obliga a leer una fecha completa, encontrar el día, y no tocar la hora sin querer.

          Estos botones corren el día y CONSERVAN la hora, que es exactamente lo que la persona
          quiere: la cena fue a las nueve, lo que estaba mal era el día. El límite de RF-07 se
          respeta acá para no ofrecer un botón que el servidor va a rechazar; el rechazo igual
          existe del otro lado, porque una regla que sólo vive en la pantalla no es una regla.
        */}
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <Eyebrow>Pasar a otro día</Eyebrow>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <Chip
              large
              disabled={!puedeCorrer(current.occurredAt, -1)}
              onClick={() => void run(() => moverDias(current, -1))}
            >
              Un día antes
            </Chip>
            <Chip
              large
              disabled={!puedeCorrer(current.occurredAt, 1)}
              onClick={() => void run(() => moverDias(current, 1))}
            >
              Un día después
            </Chip>
          </div>
        </div>

        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <Eyebrow>Qué comida</Eyebrow>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {MEAL_TYPES.map((type) => (
              <Chip
                key={type}
                large
                aria-pressed={type === current.type}
                onClick={() =>
                  void run(() =>
                    mealsApi.update(current.id, current.occurredAt, type as MealType, current.note),
                  )
                }
              >
                {mealTypeLabel(type)}
              </Chip>
            ))}
          </div>
        </div>
      </Card>

      <section className="stack">
        <h2>Ítems</h2>
        <Card size="flush">
          {current.items.map((item) => (
            <ItemRow
              key={item.id}
              mealId={current.id}
              item={item}
              onOpen={() => navigate(`/comidas/${current.id}/items/${item.id}`)}
            />
          ))}
        </Card>

        <Card size="tight">
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Field
                label="Agregar otro ítem"
                placeholder="Una fruta, un café con leche…"
                value={newItemText}
                onChange={(event) => setNewItemText(event.target.value)}
              />
            </div>
            <Button
              variant="quiet"
              disabled={newItemText.trim() === ''}
              onClick={() => {
                const freeText = newItemText.trim();
                setNewItemText('');
                void run(() => mealsApi.addItem(current.id, { freeText }));
              }}
            >
              Agregar
            </Button>
          </div>
        </Card>
      </section>

      {/* RF-15 · duplicar copia ítems y estimaciones vigentes sin volver a resolver. */}
      <Button variant="quiet" block onClick={() => void run(() => mealsApi.duplicate(current.id))}>
        Duplicar en este momento
      </Button>

      {/*
        RF-14 · borrar una comida borra sus ítems, sus fotos y sus estimaciones, y no se puede
        deshacer. Por eso el rojo y los dos pasos: es de los pocos lugares del producto donde la
        persona puede perder algo de verdad.
      */}
      {confirmingDelete ? (
        <Card>
          <Banner tone="danger">
            Se van a borrar esta comida, sus ítems, sus fotos y sus estimaciones. No se puede
            deshacer.
          </Banner>
          <div className="row">
            <Button variant="danger" onClick={() => void remove()}>
              Borrar igual
            </Button>
            <Button variant="quiet" onClick={() => setConfirmingDelete(false)}>
              Cancelar
            </Button>
          </div>
        </Card>
      ) : (
        <Button variant="plain" onClick={() => setConfirmingDelete(true)}>
          Borrar esta comida
        </Button>
      )}
    </>
  );
}
